<template>
  <div class="asc-event-report-grid">
    <asc-ag-grid-filter
      :key="`asc-event-report-grid_${gridId}`"
      :grid-options="agGridOptions"
      :column-defs="getGridColumnDefs"
      :row-data="agGridRowData"
      :grid-size="365"
    />
  </div>
</template>

<script>
import {
  AscAgGridFilter,
  AscAgGridAutocompleteFloatFilter,
  AscAgGridDateFloatFilter,
  AscTooltipCellRenderer,
} from '@/components/agGrid'
import { AscGridPropsMixin, AscGridMethodsMixin } from '@/components/mixins'
import { getFormattedDateTime } from '@/utilities/dates'
import {
  stringTextMatcher,
  dateStringMatcher,
} from '@/components/agGrid/AgGridHelperMethods'
import { getFilterOptions } from '@/components/agGrid/AgGridOptions'

export default {
  name: 'AscEventReportGrid',

  components: {
    AscAgGridFilter,
    AscAgGridAutocompleteFloatFilter,
    AscAgGridDateFloatFilter,
    AscTooltipCellRenderer,
  },

  mixins: [AscGridPropsMixin, AscGridMethodsMixin],

  props: {
    onOpenReportSummary: {
      type: Function,
      default: () => {},
    },
  },

  data() {
    return {
      agGridOptions: getFilterOptions(this, {
        defaultColDef: {
          tooltipComponent: 'AscTooltipCellRenderer',
        },
        rowSelection: 'single',
        suppressPaginationPanel: true,
        onCellClicked: this.handleCellClicked,
        onFirstDataRendered: this.handleFirstDataRendered, // This is required id for persistent filters to restore
        onGridReady: this.handleGridReady,
        onGridSizeChanged: this.handleGridSizeChanged,
      }),
    }
  },

  computed: {
    mappedValues() {
      return this.values
    },
    agGridRowData() {
      return this.mappedValues
    },
    getGridColumnDefs() {
      return [
        {
          headerName: 'Airline',
          field: 'airline',
          minWidth: 100,
          filter: 'agTextColumnFilter',
          filterParams: {
            textMatcher: stringTextMatcher,
          },
          floatingFilterComponent: 'AscAgGridAutocompleteFloatFilter',
          floatingFilterComponentParams: {
            ids: [],
            items: this.getUniqueItems('airline'),
            suppressFilterButton: true,
          },
          hide: !this.isColumnVisible(this.gridColumns, 'airline'),
        },
        {
          headerName: 'platform',
          field: 'platform',
          minWidth: 100,
          filter: 'agTextColumnFilter',
          filterParams: {
            textMatcher: stringTextMatcher,
          },
          floatingFilterComponent: 'AscAgGridAutocompleteFloatFilter',
          floatingFilterComponentParams: {
            ids: [],
            items: this.getUniqueItems('platform'),
            suppressFilterButton: true,
          },
        },
        {
          headerName: 'Report Type',
          field: 'reportType',
          minWidth: 180,
          filter: 'agTextColumnFilter',
          filterParams: {
            textMatcher: stringTextMatcher,
          },
          floatingFilterComponent: 'AscAgGridAutocompleteFloatFilter',
          floatingFilterComponentParams: {
            ids: [],
            items: this.getUniqueItems('reportType'),
            suppressFilterButton: true,
          },
        },
        {
          headerName: 'Tail',
          field: 'tailNumber',
          minWidth: 80,
          filter: 'agTextColumnFilter',
          filterParams: {
            textMatcher: stringTextMatcher,
          },
          floatingFilterComponent: 'AscAgGridAutocompleteFloatFilter',
          floatingFilterComponentParams: {
            ids: [],
            items: this.getUniqueItems('tailNumber'),
            suppressFilterButton: true,
          },
        },
        {
          headerName: 'Event ID',
          field: 'eventId',
          minWidth: 100,
          filter: 'agTextColumnFilter',
        },
        {
          headerName: 'Reason',
          field: 'eventReason',
          minWidth: 280,
          filter: 'agTextColumnFilter',
        },
        {
          headerName: 'Report Date',
          field: 'time',
          minWidth: 150,
          filter: 'agTextColumnFilter',
          sort: 'desc',
          filterParams: {
            textMatcher: dateStringMatcher,
          },
          floatingFilterComponent: 'AscAgGridDateFloatFilter',
          floatingFilterComponentParams: {
            suppressFilterButton: true,
            items: this.values,
            dateFloatFilterConfig: {
              left: true,
              transitionOrigin: 'top right',
            },
          },

          valueFormatter: data => getFormattedDateTime(data.value),
          comparator: this.sortDate,
        },
      ]
    },
  },

  watch: {
    values() {
      this.handleRefreshColWidths()
    },
  },

  methods: {
    handleCellClicked(event) {
      if (event) {
        this.onOpen(event.data)
      }
    },

    getNoRowsToShowText() {
      return this.isGridDataLoaded
        ? 'No Data Displayed'
        : 'Choose filter entry options to see reports.'
    },

    getContextMenuItems(params) {
      const reportId = params.node.data.acmfReportId
      const tailNumber = params.node.data.tailNumber
      const url = `/reportDetails/reportid/${reportId}/tail/${tailNumber}`
      const _that = this
      const result = [
        {
          name: 'Open Link in New Tab',
          action: function () {
            window.open(url, '_blank', 'noopener', 'noreferrer')
          },
        },
        {
          name: 'Open Event Reports Summary',
          action: function () {
            if (_that.onOpenReportSummary) {
              _that.onOpenReportSummary()
            }
          },
        },
        'separator',
        'copy',
        'copyWithHeaders',
        'export',
      ]

      return result
    },
  },
}
</script>

<style lang="scss">
.asc-event-report-grid {
  .asc-sort-icon {
    color: $primary_black;
    position: absolute;
    top: 14px;
    right: 16px;
  }

  .asc-filter-icon {
    color: $primary_black;
  }
}
</style>
